import { type TemperatureUnitPref, resolveTemperatureUnit, celsiusToFahrenheit } from './units';
import type { TelemetryHistoryResponse } from '../api/types';
import { parseTimestamp } from './format';
import { batteryLabel } from '../components/node-detail/presentation';


// --- Telemetry history (GET /api/history/nodes/{protocol}/{native_id}/telemetry) ---
// Merges the raw samples and hourly rollups the endpoint returns into one
// per-metric-type, per-field summary (latest value, its timestamp, the
// earliest timestamp seen, and a total sample count) — no charting
// library, just the endpoint's own columns condensed into a compact
// table. Hourly rollups extend the visible range past whatever raw
// history retention.py has already pruned (raw_json aside, both tables'
// numeric columns are used; hourly columns carry a `_avg` suffix that is
// stripped to line up with the raw field name it summarizes).
const TELEMETRY_META_KEYS = new Set([
  "id",
  "protocol",
  "node_id",
  "observer_id",
  "trust_tier",
  "observed_at",
  "metric_type",
  "created_at",
  "bucket_start",
  "sample_count",
]);


// battery_level's percentage unit isn't spelled out here (it already gets
// its own "Powered"-aware special case below, same as elsewhere on this
// page) — see displayTelemetryUnit/isPercentTelemetryField, which fold it
// in alongside whatever this map lists as "%", so a future percentage
// field only needs to be added here once.
const TELEMETRY_FIELD_UNIT: Record<string, string> = {
  voltage: "V",
  temperature: "°C",
  relative_humidity: "%",
  barometric_pressure: "hPa",
  channel_utilization: "%",
  air_util_tx: "%",
  uptime_seconds: "s",
  battery_level: "%",
};


export function titleizeTelemetryField(field: string): string {
  return field
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}


// Any field whose unit is "%" (battery_level included, via the map above)
// gets a fixed 0-100 y axis on its chart instead of auto-scaling — a
// battery that only ever ranged 0-25% must not render as if it were full.
export function isPercentTelemetryField(field: string): boolean {
  return TELEMETRY_FIELD_UNIT[field] === "%";
}


// Display-only unit: temperature is stored/returned in °C but is shown in
// whichever unit `tempPref` resolves to (lib/units.ts's resolveTemperature
// Unit — "auto" derives it from the browser's own locale; an explicit
// display.temperature_unit setting, GET /api/config/display, always wins).
// Every other unit passes through unchanged. Driven by the unit itself,
// not the field name, so soil_temperature/co2_temperature/pm_temperature/
// form_temperature get the same treatment automatically if they ever
// arrive with a °C unit here.
export function displayTelemetryUnit(field: string, tempPref: TemperatureUnitPref | undefined): string | undefined {
  const unit = TELEMETRY_FIELD_UNIT[field];
  return unit === "°C" ? `°${resolveTemperatureUnit(tempPref).toUpperCase()}` : unit;
}


function celsiusToDisplay(c: number, tempPref: TemperatureUnitPref | undefined): number {
  return resolveTemperatureUnit(tempPref) === "f" ? celsiusToFahrenheit(c) : c;
}


export function formatTelemetryValue(field: string, value: number, tempPref: TemperatureUnitPref | undefined): string {
  if (field === "battery_level") return batteryLabel(value) ?? "--";
  const unit = TELEMETRY_FIELD_UNIT[field];
  const displayValue = unit === "°C" ? celsiusToDisplay(value, tempPref) : value;
  const rounded = Number.isInteger(displayValue) ? String(displayValue) : displayValue.toFixed(2);
  const outUnit = displayTelemetryUnit(field, tempPref);
  return outUnit ? `${rounded} ${outUnit}` : rounded;
}


// --- Telemetry charts (2026-09-24 rebuild) --------------------------------
// Operator's own words: "telemetry needs to be nice looking graphs, not a
// string of cards. ONE graph for ONE type of telemetry a dot graph with
// lines to see trends." Replaces the old per-packet-reading chip-card list
// (which showed one reading's values as a snapshot, no trend) with one
// SVG chart per metric field, built from the SAME telemetry-history fetch
// the old "Telemetry history" summary table already used — see
// TelemetryChart.tsx for the actual SVG dots-plus-line plot (no charting
// library added).
//
// A fixed, sensible display order for the fields this deployment actually
// has columns for (see backend/amalgamesh/db/migrations/0005_phase2_core.sql
// — the `telemetry`/`telemetry_hourly_rollup` tables define exactly these
// eight numeric columns today; a future column not in this list still gets
// a chart, just sorted after all of these, alphabetically).
const TELEMETRY_FIELD_ORDER = [
  "battery_level",
  "voltage",
  "channel_utilization",
  "air_util_tx",
  "uptime_seconds",
  "temperature",
  "relative_humidity",
  "barometric_pressure",
];


export function telemetryFieldRank(field: string): number {
  const idx = TELEMETRY_FIELD_ORDER.indexOf(field);
  return idx === -1 ? TELEMETRY_FIELD_ORDER.length : idx;
}


interface TelemetrySeriesPoint {
  t: number;
  v: number;
  observerId: string | null;
}


interface TelemetrySeries {
  field: string;
  // Which table this series' points came from — raw preferred, hourly
  // rollup only as a fallback for a field with zero raw samples (raw is
  // pruned after retention.telemetry_raw_days; the rollup survives far
  // longer, see that migration's own comment).
  source: "raw" | "hourly";
  points: TelemetrySeriesPoint[];
}


// Pivots GET .../telemetry's {raw, hourly} rows into one time series per
// metric field. Multiple observers reporting the SAME metric (e.g. this
// node's device metrics arriving via more than one observer source) are
// the same physical sensor — merged into one
// series in time order, never split into per-observer series and never
// dropped.
export function buildTelemetrySeries(history: TelemetryHistoryResponse): Map<string, TelemetrySeries> {
  const rawByField = new Map<string, TelemetrySeriesPoint[]>();
  for (const row of history.raw ?? []) {
    const t = parseTimestamp(row.observed_at);
    if (t === null) continue;
    for (const [key, value] of Object.entries(row)) {
      if (TELEMETRY_META_KEYS.has(key) || typeof value !== "number") continue;
      let arr = rawByField.get(key);
      if (!arr) {
        arr = [];
        rawByField.set(key, arr);
      }
      arr.push({ t, v: value, observerId: row.observer_id ?? null });
    }
  }

  const hourlyByField = new Map<string, TelemetrySeriesPoint[]>();
  for (const row of history.hourly ?? []) {
    const t = parseTimestamp(row.bucket_start);
    if (t === null) continue;
    for (const [key, value] of Object.entries(row)) {
      if (!key.endsWith("_avg") || typeof value !== "number") continue;
      const field = key.slice(0, -"_avg".length);
      let arr = hourlyByField.get(field);
      if (!arr) {
        arr = [];
        hourlyByField.set(field, arr);
      }
      arr.push({ t, v: value, observerId: row.observer_id ?? null });
    }
  }

  const series = new Map<string, TelemetrySeries>();
  const fields = new Set([...rawByField.keys(), ...hourlyByField.keys()]);
  for (const field of fields) {
    const rawPoints = rawByField.get(field);
    if (rawPoints && rawPoints.length > 0) {
      rawPoints.sort((a, b) => a.t - b.t);
      series.set(field, { field, source: "raw", points: rawPoints });
      continue;
    }
    const hourlyPoints = hourlyByField.get(field);
    if (hourlyPoints && hourlyPoints.length > 0) {
      hourlyPoints.sort((a, b) => a.t - b.t);
      series.set(field, { field, source: "hourly", points: hourlyPoints });
    }
  }
  return series;
}


// Chart axis / hover time label — date + time, since a raw-history span can
// run days and an hourly-rollup span far longer; relativeTime's "X ago" (used
// elsewhere on this page) reads wrong for a fixed historical point.
export function chartTimeLabel(t: number): string {
  return new Date(t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}


interface TelemetryFieldSummary {
  latest: number;
  latestAt: string;
  earliestAt: string;
  sampleCount: number;
}


export function summarizeTelemetryHistory(history: TelemetryHistoryResponse): Map<string, Map<string, TelemetryFieldSummary>> {
  const byMetricType = new Map<string, Map<string, TelemetryFieldSummary>>();

  const touch = (metricType: string, field: string, value: number, at: string, weight: number) => {
    let byField = byMetricType.get(metricType);
    if (!byField) {
      byField = new Map();
      byMetricType.set(metricType, byField);
    }
    const existing = byField.get(field);
    if (!existing) {
      byField.set(field, { latest: value, latestAt: at, earliestAt: at, sampleCount: weight });
      return;
    }
    existing.sampleCount += weight;
    if (at >= existing.latestAt) {
      existing.latest = value;
      existing.latestAt = at;
    }
    if (at < existing.earliestAt) existing.earliestAt = at;
  };

  for (const row of history.hourly ?? []) {
    const metricType = row.metric_type ?? "unknown";
    const at = row.bucket_start ?? "";
    const weight = row.sample_count ?? 0;
    for (const [key, value] of Object.entries(row)) {
      if (!key.endsWith("_avg") || typeof value !== "number") continue;
      touch(metricType, key.slice(0, -"_avg".length), value, at, weight);
    }
  }

  for (const row of history.raw ?? []) {
    const metricType = row.metric_type ?? "unknown";
    const at = row.observed_at ?? "";
    for (const [key, value] of Object.entries(row)) {
      if (TELEMETRY_META_KEYS.has(key) || typeof value !== "number") continue;
      touch(metricType, key, value, at, 1);
    }
  }

  return byMetricType;
}
